/**
 * Indian financial year helpers — "2026-27" means 1 Apr 2026 → 31 Mar 2027.
 *
 * The shape is load-bearing, not cosmetic: the backend parses it with /^\d{4}-\d{2}$/ to derive the
 * year's date range (see fyRange in returns.service.ts), and a scheme whose FY does not parse cannot
 * be included in the year-end turnover-bonus run at all. So the UI offers a list rather than a text
 * box, and this is the one place the format is defined.
 */
export const FY_PATTERN = /^\d{4}-\d{2}$/;

/** The FY a date falls in. April (month index 3) starts the new year. */
export function currentFy(now: Date = new Date()): string {
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return fyFromStartYear(startYear);
}

export function fyFromStartYear(startYear: number): string {
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}

/** The 1 April a financial year opens on — the natural default for "effective from". */
export function fyStartDate(fy: string): string {
  return `${Number(fy.slice(0, 4))}-04-01`;
}

/**
 * A window of financial years around today, oldest first, with the live one marked.
 * `include` forces an extra value into the list (an existing record's FY, which may predate the
 * window or — for rows saved before this was a dropdown — not even be a valid year), so opening an
 * old scheme never silently rewrites what it was set to.
 */
export function fyOptions({ back = 2, forward = 2, include }: { back?: number; forward?: number; include?: string | null } = {}) {
  const cur = currentFy();
  const startYear = Number(cur.slice(0, 4));
  const years: string[] = [];
  for (let i = -back; i <= forward; i++) years.push(fyFromStartYear(startYear + i));
  const opts = years.map((fy) => ({ value: fy, label: fy === cur ? `${fy} · current` : fy }));
  const extra = (include ?? '').trim();
  if (extra && !years.includes(extra)) {
    opts.unshift({ value: extra, label: FY_PATTERN.test(extra) ? extra : `${extra} — not a valid year` });
  }
  return opts;
}
